import type { Role } from "@/auth/types";
import { apiGet, apiSend } from "@/shared/lib/api-client";
import type { AdminUser, AdminUserList, InviteUserRequest } from "../types/admin.types";

/** Admin user management, /v1/admin/users (platform admins only). */
const BASE = "/v1/admin/users";

const userPath = (username: string) => `${BASE}/${encodeURIComponent(username)}`;

export function listUsers(next: string | null): Promise<AdminUserList> {
  return apiGet<AdminUserList>(next ? `${BASE}?next=${encodeURIComponent(next)}` : BASE);
}

/** Accounts waiting for approval: signed in, enabled, no role yet. Newest first. */
export function listPendingUsers(): Promise<{ users: AdminUser[] }> {
  return apiGet<{ users: AdminUser[] }>(`${BASE}/pending`);
}

/** Creates the account; Cognito emails a temporary password. */
export function inviteUser(request: InviteUserRequest): Promise<AdminUser> {
  return apiSend<AdminUser>("POST", BASE, request);
}

export function resendInvite(username: string): Promise<void> {
  return apiSend("POST", `${userPath(username)}/resend-invite`);
}

/** One role per user; null removes it. Losing a role signs the user out. */
export function setUserRole(username: string, role: Role | null): Promise<AdminUser> {
  return apiSend<AdminUser>("PUT", `${userPath(username)}/role`, { role });
}

/** Disabling blocks sign-in and ends every session. */
export function setUserEnabled(username: string, enabled: boolean): Promise<AdminUser> {
  return apiSend<AdminUser>("PUT", `${userPath(username)}/enabled`, { enabled });
}

/** Deletes a disabled account for good (the API refuses enabled ones). */
export function deleteUser(username: string): Promise<void> {
  return apiSend("DELETE", userPath(username));
}

/** Replaces the user's access groups; applies on their next page load. */
export function setUserAccessGroups(username: string, groupIds: string[]): Promise<AdminUser> {
  return apiSend<AdminUser>("PUT", `${userPath(username)}/access-groups`, { groupIds });
}
